import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type BacktestRequest,
  BacktestResponseSchema,
  type CreateRuleRequest,
  RuleListResponseSchema,
  RuleSchema,
  type UpdateRuleRequest,
} from '@vision360/contracts';
import { useApi } from '@/lib/auth/session-context';
import { alertKeys } from '@/widgets/_sdk/hooks';

/** Alert rules over the API (TechSpec §6.1, §12; PRD VS-060). */

export const RULES_KEY = ['rules'] as const;
export const ALERTS_KEY = alertKeys.all;

export function useRules() {
  const api = useApi();
  return useQuery({
    queryKey: RULES_KEY,
    queryFn: ({ signal }) =>
      api.request('/api/v1/rules', { schema: RuleListResponseSchema, signal }),
    // A throttled rule's pause ends on the clock: re-read now and then.
    refetchInterval: 60_000,
  });
}

function useRuleMutation<V>(send: (api: ReturnType<typeof useApi>, vars: V) => Promise<unknown>) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: V) => send(api, vars),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

export const useCreateRule = () =>
  useRuleMutation((api, body: CreateRuleRequest) =>
    api.request('/api/v1/rules', { method: 'POST', body, schema: RuleSchema }),
  );

export const useUpdateRule = () =>
  useRuleMutation((api, { id, patch }: { id: string; patch: UpdateRuleRequest }) =>
    api.request(`/api/v1/rules/${id}`, { method: 'PATCH', body: patch, schema: RuleSchema }),
  );

export function useDeleteRule() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.request(`/api/v1/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RULES_KEY });
      // Deleting a rule resolves its active alerts.
      void queryClient.invalidateQueries({ queryKey: ALERTS_KEY });
    },
  });
}

/** A saved rule (`ruleId`) or an unsaved condition (`body.condition`). */
export function useBacktest(ruleId?: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (body: BacktestRequest) =>
      api.request(ruleId ? `/api/v1/rules/${ruleId}/backtest` : '/api/v1/rules/backtest', {
        method: 'POST',
        body,
        schema: BacktestResponseSchema,
      }),
  });
}
